import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    requirements: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.requirements) {
      toast.error("Please fill name, email and project requirements.");
      return;
    }
    setLoading(true);
    // Frontend-only success per user request
    await new Promise((r) => setTimeout(r, 700));
    setLoading(false);
    toast.success("Thanks! We'll reach out within 1 business day.");
    setForm({ name: "", company: "", email: "", phone: "", requirements: "" });
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 -z-10 grid-bg opacity-30" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-3">
              Contact
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Ready to transform
              <br />
              your <span className="gradient-text-vivid">business?</span>
            </h2>
            <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-md">
              Tell us what you're building. We'll come back with a custom proposal — and a free
              architecture review.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href="mailto:info@spoortitechnologies.in"
                className="flex items-center gap-4 group"
                data-testid="contact-email-link"
              >
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 grid place-items-center group-hover:border-cyan-400/50 transition">
                  <Mail className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Email</div>
                  <div className="font-heading">info@spoortitechnologies.in</div>
                </div>
              </a>
              <a
                href="https://wa.me/916364851581"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-phone-link"
              >
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 grid place-items-center group-hover:border-cyan-400/50 transition">
                  <Phone className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">WhatsApp</div>
                  <div className="font-heading">+91 63648 51581</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 grid place-items-center">
                  <MapPin className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Office</div>
                  <div className="font-heading">Bengaluru, India</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-[#131C2F] border border-white/10 rounded-2xl p-6 sm:p-8 lg:p-10"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name *">
                <input
                  data-testid={CONTACT.name}
                  value={form.name}
                  onChange={update("name")}
                  className={inputCls}
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Company">
                <input
                  data-testid={CONTACT.company}
                  value={form.company}
                  onChange={update("company")}
                  className={inputCls}
                  placeholder="Acme Industries"
                />
              </Field>
              <Field label="Work Email *">
                <input
                  data-testid={CONTACT.email}
                  type="text"
                  inputMode="email"
                  value={form.email}
                  onChange={update("email")}
                  className={inputCls}
                  placeholder="jane@acme.com"
                />
              </Field>
              <Field label="Phone">
                <input
                  data-testid={CONTACT.phone}
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputCls}
                  placeholder="+91 98765 43210"
                />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Project Requirements *">
                <textarea
                  data-testid={CONTACT.requirements}
                  rows={5}
                  value={form.requirements}
                  onChange={update("requirements")}
                  className={`${inputCls} resize-none`}
                  placeholder="Tell us about your use case, scale, timelines…"
                />
              </Field>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid={CONTACT.submit}
              className="btn-primary mt-6 w-full inline-flex items-center justify-center gap-2 text-white px-7 py-3.5 rounded-full font-semibold disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Book Free Consultation
                  <Send className="w-4 h-4" />
                </>
              )}
            </button>
            <p className="mt-3 text-xs text-slate-500 text-center">
              We respect your privacy. Your details are never shared.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-[#0B0F19] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/20 transition";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-slate-400 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
